// Nine Lives '84 - touch controls.
//
// On-screen buttons for phones and tablets: left/right on the left, jump,
// action and down on the right. Held buttons contribute command bits;
// jump and action fire once per press. Shown only on touch devices (or
// with ?touch).

import { K_ACTION, K_DOWN, K_JUMP, K_LEFT, K_PAUSE, K_RIGHT, K_UP } from '../sim/constants';

interface Button {
  label: string;
  bits: number;
  cls: string;
  /** Bits sent once per press rather than while held. */
  edge?: number;
}

const LEFT: Button[] = [
  { label: '◀', bits: K_LEFT, cls: 'left' },
  { label: '▶', bits: K_RIGHT, cls: 'right' },
];

const RIGHT: Button[] = [
  { label: 'JUMP', bits: K_UP, cls: 'jump', edge: K_JUMP },
  { label: 'ACT', bits: 0, cls: 'act', edge: K_ACTION },
  { label: '▼', bits: K_DOWN, cls: 'down' },
];

export function isTouchDevice(): boolean {
  return (
    typeof window !== 'undefined' &&
    ('ontouchstart' in window || (navigator.maxTouchPoints ?? 0) > 0)
  );
}

export class TouchInput {
  private readonly root: HTMLElement;
  private readonly held = new Map<number, number>(); // pointerId -> bits
  private pressed = 0;

  constructor(container: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'touch';
    const left = this.cluster('touch-left', LEFT);
    const right = this.cluster('touch-right', RIGHT);
    const pause = this.button({ label: '⏸', bits: 0, cls: 'pause', edge: K_PAUSE });
    pause.classList.add('touch-pause');
    this.root.append(left, right, pause);
    container.appendChild(this.root);
  }

  private cluster(cls: string, buttons: Button[]): HTMLElement {
    const box = document.createElement('div');
    box.className = `touch-cluster ${cls}`;
    for (const b of buttons) box.appendChild(this.button(b));
    return box;
  }

  private button(b: Button): HTMLElement {
    const el = document.createElement('div');
    el.className = `touch-btn ${b.cls}`;
    el.textContent = b.label;
    const down = (e: PointerEvent): void => {
      e.preventDefault();
      el.setPointerCapture(e.pointerId);
      if (b.edge) this.pressed |= b.edge;
      if (b.bits) this.held.set(e.pointerId, b.bits);
      el.classList.add('active');
    };
    const up = (e: PointerEvent): void => {
      this.held.delete(e.pointerId);
      el.classList.remove('active');
    };
    el.addEventListener('pointerdown', down);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    el.addEventListener('lostpointercapture', up);
    el.addEventListener('contextmenu', (e) => e.preventDefault());
    return el;
  }

  setVisible(v: boolean): void {
    this.root.hidden = !v;
  }

  poll(): number {
    let keys = this.pressed;
    this.pressed = 0;
    for (const bits of this.held.values()) keys |= bits;
    return keys;
  }
}
